import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { chartVariantForId, NEUTRAL_INK } from './accent'

// Every thumbnail draws into a panel of this height so the three item types line up in one grid —
// otherwise Card.Body starts at a different y per type and the row reads as ragged.
const PANEL_CONTENT_HEIGHT = 62

// Rendered height of a chart illustration, matching the 100x28 viewBox so the shapes draw at their
// true proportions. Scaling this down squashed them vertically (preserveAspectRatio="none"), which
// is why it stays tied to the viewBox rather than to the panel.
const CHART_HEIGHT = 28

// Chart illustrations for the document preview. Each renders inside a 100x28 SVG box so they
// share a footprint, and each tints to the item's accent. Purely decorative — placeholder
// shapes, not real chart data. A variant is chosen deterministically per item (see below).
const CHART_VARIANTS = [
  // Bars
  function Bars({ accent }) {
    const heights = [60, 90, 45, 75, 55]
    return (
      <svg viewBox="0 0 100 28" width="100%" height={CHART_HEIGHT} preserveAspectRatio="none">
        {heights.map((h, i) => {
          const barH = (h / 100) * 28
          return (
            <rect
              key={i}
              x={i * 20 + 2}
              y={28 - barH}
              width={16}
              height={barH}
              rx={1}
              fill={accent}
              opacity={0.55}
            />
          )
        })}
      </svg>
    )
  },
  // Line
  function Line({ accent }) {
    const pts = [26, 8, 18, 22, 6, 14]
    return (
      <svg viewBox="0 0 100 28" width="100%" height={CHART_HEIGHT} preserveAspectRatio="none">
        <polyline
          points={pts.map((y, i) => `${(i / (pts.length - 1)) * 100},${y}`).join(' ')}
          fill="none"
          stroke={accent}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity={0.7}
        />
        {pts.map((y, i) => (
          <circle key={i} cx={(i / (pts.length - 1)) * 100} cy={y} r={1.8} fill={accent} />
        ))}
      </svg>
    )
  },
  // Area
  function Area({ accent }) {
    const pts = [24, 10, 16, 6, 18, 8]
    const line = pts.map((y, i) => `${(i / (pts.length - 1)) * 100},${y}`).join(' ')
    return (
      <svg viewBox="0 0 100 28" width="100%" height={CHART_HEIGHT} preserveAspectRatio="none">
        <polygon points={`0,28 ${line} 100,28`} fill={accent} opacity={0.25} />
        <polyline points={line} fill="none" stroke={accent} strokeWidth={2} strokeLinejoin="round" opacity={0.7} />
      </svg>
    )
  },
  // Donut
  function Donut({ accent }) {
    // Two arcs (accent + light remainder) around a ring, centered in the 100x28 box.
    const cx = 50
    const cy = 14
    const r = 11
    const c = 2 * Math.PI * r
    const filled = 0.64
    return (
      <svg viewBox="0 0 100 28" width="100%" height={CHART_HEIGHT}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bs-border-color)" strokeWidth={5} />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={accent}
          strokeWidth={5}
          strokeDasharray={`${c * filled} ${c}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          opacity={0.75}
        />
      </svg>
    )
  },
  // Stacked bars
  function Stacked({ accent }) {
    const cols = [
      [10, 8],
      [6, 14],
      [12, 6],
      [8, 10],
      [5, 16]
    ]
    return (
      <svg viewBox="0 0 100 28" width="100%" height={CHART_HEIGHT} preserveAspectRatio="none">
        {cols.map(([bottom, top], i) => (
          <g key={i}>
            <rect x={i * 20 + 2} y={28 - bottom} width={16} height={bottom} rx={1} fill={accent} opacity={0.7} />
            <rect x={i * 20 + 2} y={28 - bottom - top} width={16} height={top} rx={1} fill={accent} opacity={0.3} />
          </g>
        ))}
      </svg>
    )
  }
]

export const chartVariantForReport = (id) => chartVariantForId(id, CHART_VARIANTS.length)

// Height of the accent bar that caps a Report's page. Unpaged panels absorb it into their content
// so every type still occupies the same vertical space in a mixed grid.
const PAGE_BAR_HEIGHT = 8

// The shared shell every thumbnail draws into: a light band, a type chip, and a white panel.
//
// `paged` separates a Report from its source material. A Report's panel is a page — it floats on a
// shadow and is capped by a colored bar. A View or Sample gets a flat bordered panel with no color
// and no shadow, and fills it edge to edge. So the grid reads as documents (raised, colored) among
// inputs (flat, grey), while both still sit on a defined surface: dropping the panel entirely left
// the illustration with nothing to sit against and it stopped reading as anything at all.
function ThumbnailFrame({ accent, typeIcon, typeLabel, paged = false, children }) {
  return (
    <div
      className="d-flex justify-content-center align-items-start p-3 pb-0"
      style={{
        background: 'var(--bs-light)',
        borderBottom: '1px solid var(--bs-border-color)',
        position: 'relative'
      }}
    >
      <span
        className="badge bg-white text-muted fw-normal border"
        style={{ position: 'absolute', top: 8, left: 8, fontSize: '0.65rem' }}
      >
        {/* The glyph carries the type at a glance, so it stays dark while the word beside it sits
            back as a label. Inheriting text-muted washed it out — worst on the View's table icon,
            which is fine detail and vanished into the chip once the card went monochrome. */}
        <FontAwesomeIcon icon={typeIcon} className="me-1 text-dark" />
        {typeLabel}
      </span>
      {/* No bottom padding, no bottom edge: the panel runs off the base of the band and disappears
          behind the card body, so it reads as a page continuing underneath the title rather than a
          small picture parked above it. Its dimensions are unchanged — only the bottom is open. */}
      <div
        className={`bg-white w-100 ${paged ? 'shadow-sm' : ''}`}
        style={{
          maxWidth: 160,
          borderRadius: '4px 4px 0 0',
          overflow: 'hidden',
          border: paged ? 'none' : '1px solid var(--bs-border-color)',
          borderBottom: 'none'
        }}
      >
        {paged && <div style={{ height: PAGE_BAR_HEIGHT, background: accent }} />}
        {/* A page insets its content the way a document has margins. The others fill the panel, so
            a table can run to the panel edge the way a real one does. */}
        <div
          className={paged ? 'p-2' : ''}
          style={{ height: paged ? PANEL_CONTENT_HEIGHT : PANEL_CONTENT_HEIGHT + PAGE_BAR_HEIGHT }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

// Report — a headed document with a chart in it. The chart style varies per report so the index
// grid doesn't read as identical cards.
export function DocumentThumbnail({ accent, id, typeIcon, typeLabel }) {
  const Chart = CHART_VARIANTS[chartVariantForReport(id)] || CHART_VARIANTS[0]
  return (
    <ThumbnailFrame accent={accent} typeIcon={typeIcon} typeLabel={typeLabel} paged>
      <div className="mb-2" style={{ height: 6, width: '70%', background: accent, opacity: 0.85, borderRadius: 2 }} />
      <div className="mb-1" style={{ height: 4, width: '100%', background: 'var(--bs-border-color)', borderRadius: 2 }} />
      <div className="mb-2" style={{ height: 4, width: '90%', background: 'var(--bs-border-color)', borderRadius: 2 }} />
      <div className="d-flex align-items-end" style={{ height: CHART_HEIGHT }}>
        <Chart accent={accent} />
      </div>
    </ThumbnailFrame>
  )
}

// View — a dataset, drawn as an actual table: a solid dark header band over ruled cells that run to
// the panel edge. The previous version floated detached bars with no rules between them, which at
// this size resolved into a grey smudge rather than anything table-shaped. Column dividers are what
// make a grid legible when it is only ~70px tall, so they carry the whole illustration.
const TABLE_COLUMNS = [3, 2, 2]
const TABLE_ROWS = [0, 1, 2, 3]

export function DatasetThumbnail({ typeIcon, typeLabel }) {
  // The grid is the whole illustration, so the rules are drawn heavier than a real table's would
  // be — at ~14px per row a hairline reads as noise rather than structure.
  const RULE = '2px solid var(--bs-border-color)'
  const divider = (index) => (index < TABLE_COLUMNS.length - 1 ? RULE : 'none')

  return (
    <ThumbnailFrame typeIcon={typeIcon} typeLabel={typeLabel}>
      <div className="d-flex flex-column h-100">
        <div className="d-flex flex-shrink-0" style={{ height: 12, background: NEUTRAL_INK, opacity: 0.72 }}>
          {TABLE_COLUMNS.map((flex, i) => (
            <div
              key={i}
              style={{
                flex,
                // Rules inside the header have to be light — a border-color divider disappears
                // against the dark band.
                borderRight: i < TABLE_COLUMNS.length - 1 ? '2px solid rgba(255, 255, 255, 0.45)' : 'none'
              }}
            />
          ))}
        </div>
        {TABLE_ROWS.map((row) => (
          <div
            className="d-flex flex-fill"
            key={row}
            style={{ borderTop: RULE }}
          >
            {TABLE_COLUMNS.map((flex, i) => (
              <div key={i} style={{ flex, borderRight: divider(i) }} />
            ))}
          </div>
        ))}
      </div>
    </ThumbnailFrame>
  )
}

// Sample — a set of transactions pulled for review. Ticked rows over a progress bar showing how
// much of the sample has actually been reviewed (the one thumbnail carrying real data). Unpaged and
// monochrome like the View: it is material a report draws on, not a report.
export function SampleThumbnail({ typeIcon, typeLabel, completeCount = 0, totalCount = 0 }) {
  const pct = totalCount > 0 ? Math.round((completeCount / totalCount) * 100) : 0
  return (
    <ThumbnailFrame typeIcon={typeIcon} typeLabel={typeLabel}>
      {/* The unpaged panel has no padding of its own so a table can reach its edge; this one insets
          its own content instead. */}
      <div className="d-flex flex-column h-100 p-2">
        {[0, 1, 2].map((row) => {
          const ticked = totalCount > 0 && row < Math.round((pct / 100) * 3)
          return (
            <div className="d-flex align-items-center gap-1 mb-1" key={row}>
              <div
                style={{
                  width: 6,
                  height: 6,
                  flexShrink: 0,
                  borderRadius: 1,
                  background: ticked ? NEUTRAL_INK : 'transparent',
                  border: ticked ? 'none' : '1px solid var(--bs-border-color)',
                  opacity: ticked ? 0.82 : 1
                }}
              />
              <div
                style={{
                  height: 5,
                  flex: 1,
                  background: 'var(--bs-border-color)',
                  borderRadius: 2,
                  opacity: 1 - row * 0.18
                }}
              />
            </div>
          )
        })}
        <div
          className="mt-auto"
          style={{ height: 5, background: 'var(--bs-border-color)', borderRadius: 2, overflow: 'hidden' }}
        >
          <div style={{ height: '100%', width: `${pct}%`, background: NEUTRAL_INK, opacity: 0.82 }} />
        </div>
      </div>
    </ThumbnailFrame>
  )
}
